import { ActivityEntityName, EntityViewDisplay, manageRecurringJob, RecurringJob, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { overrideRecurringJobCellRenderer, recurringJobColumnOverrides } from './board/recurring.job.board.cell.renderers';
import { recurringJobRunningTag, recurringJobStatusTag } from './presentation/recurring.job.status.colour';
import { RecurringJobRelationName } from './relation.names';
import { navigateToRecurringJobManage, RECURRING_JOB_ALL_VIEW_ID, RECURRING_JOB_SLUG, recurringJobRecordRoute } from './services/recurring.job.board.open';

/**
 * Recurring Job manifest: table board, creator dialog, Manage page, and the boards Manage mounts.
 *
 * These surfaces are staged under finance-pro alongside the other v2 boards. The legacy
 * `/jobs/recurring` dashboard and manage page are untouched and stay the live UI until the Manage
 * surface carries Schedules, Items and Tasks.
 */
export const recurringJobManifest = registerManifest<RecurringJob>({
  entity: ActivityEntityName.RecurringJob,
  slug: RECURRING_JOB_SLUG,

  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      header: {
        title: 'Recurring Jobs',
        showPaginator: true,
        // All, not the server default: "Active Recurring Jobs" is the narrower list, and this board
        // is the one that will carry the Active/Inactive segments. Board-context only, so the
        // legacy /jobs/recurring board keeps whatever the server or the user picked.
        defaultViewId: RECURRING_JOB_ALL_VIEW_ID,
      },
      itemInteraction: {
        navigate: navigateToRecurringJobManage,
      },
      table: {
        columns: recurringJobColumnOverrides,
        overrideCellRenderer: overrideRecurringJobCellRenderer,
      },
    },
  },

  surfaces: {
    [SurfaceType.Creator]: () => import('./surface/creator/recurring.job.creator.surface').then(module => module.recurringJobCreatorSurface),
    [SurfaceType.Manage]: () => import('./surface/manage/recurring.job.manage.surface').then(module => module.recurringJobManageSurface),
  },

  operations: {
    // The legacy quick-create form stays where EntityCreatorService opens it (the /jobs/recurring
    // dashboard, entity links, pickers); this board opens the Creator surface instead. Both write
    // through the same RecurringJob QuickCreate command.
    create: {
      surface: SurfaceType.Creator,
      presentation: { mode: 'dialog', width: 'min(1200px, calc(100vw - 48px))', height: 'min(800px, calc(100vh - 48px))', maxHeight: 'calc(100vh - 48px)' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(...manageRecurringJob),
    },
    edit: { surface: SurfaceType.Manage },
  },

  routes: {
    root: '/finance-pro/recurring-jobs',
    record: recurringJobRecordRoute,
  },

  relations: {
    [RecurringJobRelationName.Items]: () => import('./relations/recurring.job.items.relation').then(module => module.recurringJobItemsBoardBuilder),
    [RecurringJobRelationName.Tasks]: () => import('./relations/recurring.job.tasks.relation').then(module => module.recurringJobTasksBoardBuilder),
    [ActivityEntityName.Job]: () => import('./relations/recurring.job.jobs.relation').then(module => module.recurringJobJobsBoardBuilder),
    [ActivityEntityName.AssetLink]: () => import('./relations/recurring.job.assets.relation').then(module => module.recurringJobAssetsBoardBuilder),
    [ActivityEntityName.Activity]: () => import('./relations/recurring.job.activities.relation').then(module => module.recurringJobActivitiesBoardBuilder),
  },

  presentation: {
    tags: {
      status: recurringJobStatusTag,
      turnOn: recurringJobRunningTag,
    },
  },
});
